import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import { parseArticle, type Article } from '@/lib/types';
import { SITE_TAG, AUTHOR_PUBKEY } from '@/lib/categories';

export function useArticles(category?: string) {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['articles', category],
    queryFn: async () => {
      const events = await nostr.query([
        {
          kinds: [30023],
          authors: [AUTHOR_PUBKEY],
          '#t': [SITE_TAG],
          limit: 100,
        },
      ]);

      const articles: Article[] = [];
      for (const event of events) {
        const article = parseArticle(event);
        if (article) {
          // Filter by category if specified
          if (category && article.category !== category) {
            continue;
          }
          articles.push(article);
        }
      }

      // Sort by published date (oldest first - for learning progression)
      return articles.sort((a, b) => a.publishedAt - b.publishedAt);
    },
  });
}

export function useArticle(slug: string) {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['article', slug],
    queryFn: async () => {
      const events = await nostr.query([
        {
          kinds: [30023],
          authors: [AUTHOR_PUBKEY],
          '#d': [slug],
          limit: 1,
        },
      ]);

      if (events.length === 0) {
        return null;
      }

      return parseArticle(events[0]);
    },
    enabled: !!slug,
  });
}

export function useLatestArticles(limit: number = 5) {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['latest-articles', limit],
    queryFn: async () => {
      const events = await nostr.query([
        {
          kinds: [30023],
          authors: [AUTHOR_PUBKEY],
          '#t': [SITE_TAG],
          limit,
        },
      ]);

      const articles: Article[] = [];
      for (const event of events) {
        const article = parseArticle(event);
        if (article) {
          articles.push(article);
        }
      }

      return articles.sort((a, b) => b.publishedAt - a.publishedAt);
    },
  });
}
